import { ObjectId } from '@freightview/object-id'
import { sum } from 'lodash-es'

import type {
  DeliveryOption,
  EmergencyContact,
  LoadEquipment,
} from '@fv/client-types'
import type { LoadBillTo, LoadItem } from '@fv/models'
import type { DTO } from '@fv/models/core'
import type { EquipmentType } from '@fv/models/equipment'

import type { ApiQuoteRequestDTO, ApiQuoteRequestEquipmentDTO } from '@/types'
import { buildQuoteRequestShares } from '@/utils/shares'

import { billToMapper } from '../billTo/billToFuncs'
import { buildApiHandlingUnitFromFormCommodity } from '../commodities/buildApiHandlingUnit'
import { buildFormCommodity } from '../commodities/commodityUtils'
import type { FormCommodity } from '../commodities/FormCommodity'
import {
  buildHandlingUnitDTO,
  unitIsHazardous,
} from '../commodities/load-items/loadItemHelpers'
import type { LoadHandlingUnitFormModel } from '../commodities/load-items/types'
import type { CustomsBroker } from '../customsBroker/brokerTypes'
import { validateEmergencyContact } from '../emergencyContact/contactFuncs'
import { loadTemplateToQuoteRequestFields } from '../load-templates/utils'
import type { QuotePageLoad } from '../quote/types'
import type { ShipmentEquipmentFormModel } from '../shipment-equipment-form/types'
import { toApiLocations } from '../shipment-location-form/types'
import type { QuoteFormLoad, QuoteFormState, QuoteRequestLoads } from './types'

export function quoteFormLoadFromEquipment(
  equipment: ShipmentEquipmentFormModel,
): QuoteFormLoad {
  const load = buildLoad(equipment.type)

  return {
    ...load,
    accessorials: equipment.accessorials ?? [],
    alternateTypes: equipment.alternateTypes ?? [],
    isActiveInUI: true,
    ...(equipment.description && { description: equipment.description }),
    ...(equipment.declaredValue && {
      declaredValue: equipment.declaredValue.toString(),
    }),
    ...(equipment.weight && { weight: equipment.weight.toString() }),
  }
}

export function setActiveLoadItems(
  loads: QuoteRequestLoads,
  items: FormCommodity[],
): QuoteRequestLoads {
  const nextLoads = new Map(loads)

  for (const [type, loadsOfType] of nextLoads) {
    const activeIx = loadsOfType.findIndex(x => x.isActiveInUI)
    if (activeIx < 0) continue

    nextLoads.set(
      type,
      loadsOfType.map((load, ix) =>
        ix === activeIx ? { ...load, items } : load,
      ),
    )
    break
  }

  return nextLoads
}

export function buildLoad(type: EquipmentType): QuoteFormLoad {
  return {
    accessorials: [],
    alternateTypes: [],
    declaredValue: '',
    declaredValueCurrency: 'usd',
    description: '',
    isHazardous: false,
    items: [],
    loadId: new ObjectId().toHexString(),
    mode: 'truckload',
    type,
    weight: '',
    weightUOM: 'lbs',

    // Front-end only
    isActiveInUI: false,
    formRef: null, // Set from form component
    revalidationDelay: null,
  }
}

export function countLoads(loads: QuoteRequestLoads) {
  return sum(Array.from(loads.values()).map(loadsOfType => loadsOfType.length))
}

export function getHasItems(loads: QuoteRequestLoads) {
  return Array.from(loads.values()).some(loadsOfType =>
    loadsOfType.some(x => !!x.items.length),
  )
}

export function getItemsBySequence(
  loads: QuoteRequestLoads,
  sequence: number,
): FormCommodity[] {
  const itemsBySequence: FormCommodity[] = []

  for (const type of loads.keys()) {
    const loadsOfType = loads.get(type) ?? []
    const itemsOfType = loadsOfType
      .flatMap(x => x.items)
      .filter(i => i.pickSequence === sequence || i.dropSequence === sequence)

    itemsBySequence.push(...itemsOfType)
  }

  // Sort deliveries to front
  return itemsBySequence.sort(a => {
    if (a.dropSequence === sequence) return -1
    if (a.pickSequence === sequence) return 1
    return 0
  })
}

export function equipmentToFormLoad({
  billTo,
  customsBroker,
  emergencyContact,
  equipment,
  items,
  loadId,
}: {
  billTo?: DTO<LoadBillTo>
  customsBroker?: CustomsBroker[]
  emergencyContact?: EmergencyContact
  equipment: LoadEquipment
  items?: DTO<LoadItem>[]
  loadId: string
}): QuoteFormLoad {
  const billToDTO = billToMapper.loadToDTO(billTo)

  return {
    accessorials: (equipment.accessorials ?? []).map(a => ({
      key: a.key,
      ...('uom' in a && { uom: a.uom }),
      ...('count' in a && typeof a.count === 'number' && { count: a.count }),
      ...('max' in a && typeof a.max === 'number' && { max: a.max }),
      ...('min' in a && typeof a.min === 'number' && { min: a.min }),
      ...('size' in a && typeof a.size === 'number' && { size: a.size }),
    })),
    alternateTypes: equipment.alternateTypes ?? [],
    declaredValue: String(equipment.declaredValue || ''),
    declaredValueCurrency: equipment.declaredValueCurrency,
    description: equipment.description,
    loadId,
    formRef: null,
    isActiveInUI: false,
    isHazardous: equipment.isHazardous,
    items: (items ?? []).map(i =>
      buildFormCommodity({
        dropSequence: i.dropLocationSequence,
        item: i,
        pickSequence: i.pickupLocationSequence,
      }),
    ),
    mode: equipment.mode,
    revalidationDelay: null,
    type: equipment.type,
    weight: String(equipment.weight),
    weightUOM: equipment.weightUOM,
    ...(billToDTO && { billTo: billToDTO }),
    ...(customsBroker && { customsBroker }),
    ...(validateEmergencyContact(emergencyContact) && { emergencyContact }),
  }
}

function formLoadToApiEquipmentDTO(
  load: QuoteFormLoad,
): ApiQuoteRequestEquipmentDTO {
  const {
    billTo,
    customsBroker,
    declaredValue,
    description,
    emergencyContact,
    formRef: _formRef,
    isActiveInUI: _isActiveInUI,
    items,
    revalidationDelay: _revalidationDelay,
    targetPrice: _targetPrice,
    weight,
    ...equipment
  } = load
  const billToDTO = billToMapper.loadToDTO(billTo)

  return {
    ...equipment,
    description: description.trim(),
    weight: Number(weight),
    items: items.map(item =>
      buildApiHandlingUnitFromFormCommodity(item, load.mode),
    ),
    ...(billToDTO && { billTo: billToDTO }),
    ...(customsBroker && { customsBroker }),
    ...(declaredValue && { declaredValue: Number(declaredValue) }),
    ...(validateEmergencyContact(emergencyContact) && { emergencyContact }),
  }
}

export function buildQuoteRequestDTO({
  isLiveLoad,
  loads,
  orderNumber,
  selectedTemplate,
  stops,
}: QuoteFormState): ApiQuoteRequestDTO {
  return {
    equipment: Array.from(loads.values()).flatMap(loadsOfType =>
      loadsOfType.map(formLoadToApiEquipmentDTO),
    ),
    isLiveLoad,
    locations: toApiLocations(stops),
    orderNumber: orderNumber.trim(),
    workflow: 'truckload',
    ...loadTemplateToQuoteRequestFields(selectedTemplate),
    ...buildQuoteRequestShares(stops),
  }
}

export function buildQuoteEquipmentFromHandlingUnits({
  deliveryOption,
  items,
  load,
}: {
  deliveryOption?: DeliveryOption
  items: LoadHandlingUnitFormModel[]
  load: QuotePageLoad
}): ApiQuoteRequestEquipmentDTO {
  const mappedItems = items.map(buildHandlingUnitDTO(load.workflow))
  const declaredValue = items.reduce(
    (value, item) => value + Number(item.declaredValue || 0),
    0,
  )

  return {
    accessorials: [
      ...(load.protectFromFreezing
        ? [{ key: 'protect-from-freezing' as const }]
        : []),
      ...(load.singleShipment ? [{ key: 'single-shipment' as const }] : []),
    ],
    alternateTypes: [],
    declaredValueCurrency: 'usd',
    ...(declaredValue && { declaredValue }),
    ...(deliveryOption && { deliveryOption }),
    description: items[0]?.description?.trim() ?? '',
    isHazardous: items.some(unitIsHazardous),
    items: mappedItems,
    loadId: load.loadId,
    mode: load.workflow,
    type: load.workflow === 'truckload' ? 'ltl' : load.workflow,
    weight: sum(mappedItems.map(i => i.weight)),
    weightUOM: 'lbs',
  }
}
